/**
 * PipelineFlowLines — horizontal "river" view of Leads / Opportunities / Deals
 * pipelines. Each stage shows count + value + 3 last-activity samples on hover.
 *
 * Hits /api/dashboard-cockpit/pipeline-flow once and renders 3 lines.
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Loader2, ChevronRight, TrendingUp, ArrowRight, Activity,
} from 'lucide-react';
import LiveSyncBadge from '../LiveSyncBadge';
import useSmartPolling from '../../../hooks/useSmartPolling';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TONE_CLASS = {
  slate:   'from-slate-100 to-slate-50 text-slate-700 border-slate-200',
  sky:     'from-sky-100 to-sky-50 text-sky-700 border-sky-200',
  indigo:  'from-indigo-100 to-indigo-50 text-indigo-700 border-indigo-200',
  amber:   'from-amber-100 to-amber-50 text-amber-700 border-amber-200',
  emerald: 'from-emerald-100 to-emerald-50 text-emerald-700 border-emerald-200',
  rose:    'from-rose-100 to-rose-50 text-rose-700 border-rose-200',
};
const TONE_DOT = {
  slate: 'bg-slate-400', sky: 'bg-sky-500', indigo: 'bg-indigo-500',
  amber: 'bg-amber-500', emerald: 'bg-emerald-500', rose: 'bg-rose-500',
};

const fmtMoney = (v) => {
  const n = Number(v || 0);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}k`;
  return n.toFixed(0);
};

export const PipelineFlowLines = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null); // { line, stage }

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
  }), []);

  const fetchFlow = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/dashboard-cockpit/pipeline-flow`, auth);
      setData(r.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const { lastSync, refreshing, refresh } = useSmartPolling(fetchFlow, {
    intervalMs: 30000, enabled: true,
  });

  if (loading) {
    return (
      <div className="bg-white border border-slate-100 rounded-xl p-6
        text-slate-400 flex items-center gap-2 mb-4">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading pipelines…
      </div>
    );
  }
  if (!data) return null;

  const lines = [
    { key: 'leads',         icon: Activity,    color: '#0EA5E9', ...data.leads,         showValue: false },
    { key: 'opportunities', icon: TrendingUp,  color: '#8B5CF6', ...data.opportunities, showValue: true },
    { key: 'deals',         icon: ChevronRight,color: '#F59E0B', ...data.deals,         showValue: true },
  ];

  return (
    <div className="space-y-3 mb-4" data-testid="pipeline-flow-lines">
      <div className="flex items-center justify-between px-1">
        <span className="text-[10px] uppercase tracking-[0.3em] text-slate-400 font-semibold">
          Live Pipelines
        </span>
        <LiveSyncBadge lastSync={lastSync} refreshing={refreshing}
          onRefresh={refresh} testId="cockpit-pipeline-live-sync" />
      </div>
      {lines.map((line) => {
        const Icon = line.icon;
        const total = (line.stages || []).reduce((a, s) => a + (s.count || 0), 0);
        const totalValue = (line.stages || []).reduce((a, s) => a + (s.value || 0), 0);
        return (
          <div key={line.key}
            data-testid={`pipeline-line-${line.key}`}
            className="bg-white rounded-xl border border-slate-100 shadow-sm
              hover:shadow-md transition-shadow overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-50">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                  style={{ background: `${line.color}20` }}>
                  <Icon className="w-3.5 h-3.5" style={{ color: line.color }} />
                </div>
                <div>
                  <div className="text-sm font-bold text-[#0F172A]">{line.title}</div>
                  <div className="text-[10px] text-slate-500">{line.subtitle}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-slate-500">
                  Total: <span className="font-bold text-[#0F172A] tabular-nums">{total}</span>
                  {line.showValue && totalValue > 0 && (
                    <span className="ml-2 text-emerald-600">{fmtMoney(totalValue)} AED</span>
                  )}
                </span>
                <Link to={line.deep_link}
                  data-testid={`pipeline-${line.key}-deep`}
                  className="flex items-center gap-1 text-[#023E8A] hover:text-[#0EA5E9] font-semibold">
                  Open <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>

            {/* Stages river */}
            <div className="flex flex-wrap gap-2 p-3">
              {(line.stages || []).map((s, idx) => {
                const cls = TONE_CLASS[s.tone] || TONE_CLASS.slate;
                const hov = hovered && hovered.line === line.key && hovered.stage === s.key;
                return (
                  <div key={s.key} className="relative flex-1 min-w-[120px]">
                    <Link to={`${line.deep_link}?stage=${encodeURIComponent(s.key)}`}
                      onMouseEnter={() => setHovered({ line: line.key, stage: s.key })}
                      onMouseLeave={() => setHovered(null)}
                      data-testid={`pipeline-stage-${line.key}-${s.key}`}
                      className={`block bg-gradient-to-br ${cls} border rounded-lg p-2.5
                        hover:shadow-md hover:-translate-y-0.5 transition-all`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] uppercase font-bold tracking-wider">
                          {s.label}
                        </span>
                        <span className={`w-1.5 h-1.5 rounded-full ${TONE_DOT[s.tone]}`} />
                      </div>
                      <div className="text-2xl font-bold tabular-nums">{s.count}</div>
                      {line.showValue && s.value > 0 && (
                        <div className="text-[10px] font-semibold opacity-70 mt-0.5">
                          {fmtMoney(s.value)} AED
                        </div>
                      )}
                    </Link>
                    {/* Hover preview with last 3 entries */}
                    {hov && s.samples && s.samples.length > 0 && (
                      <div className="absolute z-30 left-0 top-full mt-1 w-64
                        bg-[#0F172A] text-white rounded-lg shadow-2xl border
                        border-white/10 p-3 pointer-events-none">
                        <div className="text-[10px] uppercase tracking-wider text-[#C9A86A] mb-1.5">
                          Latest in {s.label}
                        </div>
                        <ul className="space-y-1">
                          {s.samples.map((x, i) => (
                            <li key={i} className="text-xs flex items-center justify-between gap-2">
                              <span className="truncate">{x.label}</span>
                              {x.value && (
                                <span className="text-emerald-400 tabular-nums text-[10px] flex-shrink-0">
                                  {fmtMoney(x.value)}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {/* Connector arrow */}
                    {idx < (line.stages.length - 1) && (
                      <ChevronRight className="absolute -right-2 top-1/2 -translate-y-1/2
                        w-3 h-3 text-slate-300 hidden lg:block" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PipelineFlowLines;
